import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, CircularProgress, Alert, Chip, Stack, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControlLabel, Switch, List, ListItem, ListItemText, Divider,
} from '@mui/material';
import {
  Warehouse as WarehouseIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Visibility as ViewIcon, MoveToInbox as ReceiveIcon, Tune as AdjustIcon, Close as CloseIcon,
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const MT = {
  in: 'Приход', out: 'Списание', transfer: 'Перемещение',
  adjust: 'Корректировка', reserve: 'Резерв', unreserve: 'Снятие резерва',
};

function Warehouse() {
  const { user } = useAuth();
  const can = (p) => user?.is_superuser || user?.permissions?.includes(p);
  const canReceive = can('warehouse.receive');
  const canAdjust = can('warehouse.adjust');

  const [rows, setRows] = useState([]);
  const [lowCount, setLowCount] = useState(0);
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [q, setQ] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [action, setAction] = useState(null);  // {item, kind:'receive'|'adjust'}

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get('/stock/', { params: { q: q || undefined, low_only: lowOnly || undefined } });
      setRows(res.data.items || []);
      setLowCount(res.data.low_stock_count || 0);
    } catch (err) { setError(err.response?.data?.detail || err.message); }
    finally { setLoading(false); }
  }, [q, lowOnly]);

  const loadRefs = useCallback(async () => {
    try {
      const [t, l] = await Promise.all([axios.get('/assets/types'), axios.get('/locations/')]);
      setTypes(t.data || []);
      setLocations(l.data.items || l.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  const del = async (it) => {
    if (!window.confirm(`Удалить позицию «${it.name}»?`)) return;
    try { await axios.delete(`/stock/${it.id}`); await load(); }
    catch (err) { setError(err.response?.data?.detail || err.message); }
  };
  const openDetail = async (it) => {
    try { const r = await axios.get(`/stock/${it.id}`); setDetail(r.data); }
    catch (err) { setError(err.response?.data?.detail || err.message); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarehouseIcon color="primary" />
          <Typography variant="h4">Склад</Typography>
          {lowCount > 0 && <Chip color="warning" size="small" label={`${lowCount} ниже порога`} />}
        </Box>
        {canReceive && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setForm({ tracking_mode: 'fungible' })}>Добавить позицию</Button>}
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Остаток меняется только через журнал движений. <b>Выдать со склада можно лишь по одобренной заявке</b>:
        одобрение резервирует количество, применение — списывает. Приход и инвентаризацию ведёт кладовщик.
      </Alert>

      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <TextField size="small" label="Поиск (название / SKU)" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 280 }} />
        <FormControlLabel control={<Switch checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} />} label="Только ниже порога" />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">Позиций нет. {canReceive && 'Нажмите «Добавить позицию».'}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Позиция', 'Тип', 'На руках', 'Резерв', 'Доступно', 'Ед.', 'Где', 'Действия'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 'bold' }} align={h === 'Действия' ? 'right' : 'left'}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((it) => (
                <TableRow key={it.id} hover sx={it.low_stock ? { bgcolor: '#fff7ed' } : {}}>
                  <TableCell sx={{ cursor: 'pointer', fontWeight: 500 }} onClick={() => openDetail(it)}>
                    {it.name}{it.sku ? ` · ${it.sku}` : ''}
                    {it.low_stock && <Chip size="small" color="warning" label="мало" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{it.asset_type_name || '—'}</TableCell>
                  <TableCell>{it.qty_on_hand}</TableCell>
                  <TableCell>{it.qty_reserved || 0}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{it.qty_available}</TableCell>
                  <TableCell>{it.unit || '—'}</TableCell>
                  <TableCell>{it.location_name || '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Подробнее / движения"><IconButton size="small" onClick={() => openDetail(it)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                    {canReceive && <Tooltip title="Приход"><IconButton size="small" color="success" onClick={() => setAction({ item: it, kind: 'receive' })}><ReceiveIcon fontSize="small" /></IconButton></Tooltip>}
                    {canAdjust && <Tooltip title="Корректировка"><IconButton size="small" onClick={() => setAction({ item: it, kind: 'adjust' })}><AdjustIcon fontSize="small" /></IconButton></Tooltip>}
                    {canAdjust && <Tooltip title="Изменить"><IconButton size="small" onClick={() => setForm(it)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                    {canAdjust && <Tooltip title="Удалить"><IconButton size="small" color="error" onClick={() => del(it)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {form && <StockForm initial={form} types={types} locations={locations}
        onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} setError={setError} />}
      {action && <QtyDialog action={action} onClose={() => setAction(null)}
        onDone={() => { setAction(null); load(); }} setError={setError} />}
      {detail && <DetailDialog item={detail} onClose={() => setDetail(null)} />}
    </Box>
  );
}

function StockForm({ initial, types, locations, onClose, onSaved, setError }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({
    name: initial.name || '', sku: initial.sku || '', asset_type_id: initial.asset_type_id || '',
    location_id: initial.location_id || '', tracking_mode: initial.tracking_mode || 'fungible',
    unit: initial.unit || 'шт', reorder_threshold: initial.reorder_threshold ?? '',
    qty_on_hand: '', notes: initial.notes || '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const save = async () => {
    setBusy(true); setError('');
    try {
      const payload = { ...f };
      ['asset_type_id', 'location_id'].forEach((k) => { if (payload[k] === '') payload[k] = null; });
      payload.reorder_threshold = payload.reorder_threshold === '' ? null : Number(payload.reorder_threshold);
      if (isEdit) {
        delete payload.qty_on_hand;
        await axios.put(`/stock/${initial.id}`, payload);
      } else {
        payload.qty_on_hand = payload.qty_on_hand === '' ? 0 : Number(payload.qty_on_hand);
        await axios.post('/stock/', payload);
      }
      onSaved();
    } catch (err) { setError(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? `Позиция «${initial.name}»` : 'Новая позиция склада'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Название" value={f.name} onChange={set('name')} required />
          <Stack direction="row" spacing={2}>
            <TextField label="SKU / артикул" value={f.sku} onChange={set('sku')} sx={{ flex: 1 }} />
            <TextField label="Единица" value={f.unit} onChange={set('unit')} sx={{ width: 120 }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField select label="Тип актива" value={f.asset_type_id} onChange={set('asset_type_id')} sx={{ flex: 1 }}>
              <MenuItem value="">— нет —</MenuItem>
              {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </TextField>
            <TextField select label="Локация (склад)" value={f.location_id} onChange={set('location_id')} sx={{ flex: 1 }}>
              <MenuItem value="">— нет —</MenuItem>
              {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField select label="Учёт" value={f.tracking_mode} onChange={set('tracking_mode')} sx={{ flex: 1 }}>
              <MenuItem value="fungible">По количеству</MenuItem>
              <MenuItem value="serialized">По серийникам</MenuItem>
            </TextField>
            <TextField type="number" label="Порог пополнения" value={f.reorder_threshold} onChange={set('reorder_threshold')} sx={{ flex: 1 }} />
            {!isEdit && <TextField type="number" label="Начальный остаток" value={f.qty_on_hand} onChange={set('qty_on_hand')} sx={{ flex: 1 }} />}
          </Stack>
          <TextField label="Заметки" value={f.notes} onChange={set('notes')} multiline minRows={1} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={busy || !f.name}>Сохранить</Button>
      </DialogActions>
    </Dialog>
  );
}

function QtyDialog({ action, onClose, onDone, setError }) {
  const { item, kind } = action;
  const [qty, setQty] = useState(kind === 'adjust' ? item.qty_on_hand : '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true); setError('');
    try {
      if (kind === 'receive') await axios.post(`/stock/${item.id}/receive`, { qty: Number(qty), reason: reason || null });
      else await axios.post(`/stock/${item.id}/adjust`, { new_qty: Number(qty), reason });
      onDone();
    } catch (err) { setError(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{kind === 'receive' ? 'Приход на склад' : 'Корректировка остатка'} — {item.name}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField type="number" label={kind === 'receive' ? 'Сколько поступило' : 'Новый остаток'} value={qty} onChange={(e) => setQty(e.target.value)} autoFocus />
          <TextField label={kind === 'adjust' ? 'Причина (обязательно)' : 'Причина / накладная'} value={reason} onChange={(e) => setReason(e.target.value)} required={kind === 'adjust'} />
          <Typography variant="caption" color="text.secondary">Сейчас на руках: {item.qty_on_hand}, доступно: {item.qty_available}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Отмена</Button>
        <Button variant="contained" onClick={go} disabled={busy || qty === '' || (kind === 'adjust' && !reason.trim())}>Применить</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailDialog({ item, onClose }) {
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Склад: {item.name}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip size="small" label={`На руках: ${item.qty_on_hand}`} />
          <Chip size="small" color="warning" label={`Резерв: ${item.qty_reserved || 0}`} />
          <Chip size="small" color="success" label={`Доступно: ${item.qty_available}`} />
          {item.asset_type_name && <Chip size="small" variant="outlined" label={item.asset_type_name} />}
          {item.location_name && <Chip size="small" variant="outlined" label={item.location_name} />}
        </Stack>
        <Typography variant="subtitle2" gutterBottom>Движения ({item.movements?.length || 0})</Typography>
        <List dense>
          {(item.movements || []).map((m) => (
            <ListItem key={m.id} sx={{ borderLeft: '3px solid #1976d2', mb: 0.5, bgcolor: '#f8fafc' }}>
              <ListItemText
                primary={`${MT[m.movement_type] || m.movement_type}: ${m.qty}${m.balance_after != null ? ` → остаток ${m.balance_after}` : ''}`}
                secondary={`${new Date(m.created_at).toLocaleString('ru-RU')}${m.performed_by ? ' · ' + m.performed_by.full_name : ''}${m.change_request_id ? ' · заявка #' + m.change_request_id : ''}${m.reason ? ' · ' + m.reason : ''}`}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Закрыть</Button></DialogActions>
    </Dialog>
  );
}

export default Warehouse;
